"use client";

import { Suspense, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { appStorage } from "../utils/appStorage";

/**
 * アプリごとにfaviconとmanifestを動的に切り替えるコンポーネント
 *
 * ファイル命名規則:
 * - favicon: /favicon-{appName}.ico または /favicon-{appName}.png
 * - manifest: /manifest-{appName}.json
 *
 * appNameの取得優先順位:
 * 1. sessionStorage (ログイン後)
 * 2. URLパラメータ ?app= (ログイン画面)
 * 3. デフォルト /favicon.ico, /manifest.json
 */

// アプリごとのfavicon拡張子マッピング
const FAVICON_EXTENSIONS = {
  smartenglish: "ico",
  movieenglish: "png",
  aienglish: "png",
  englishegg: "png",
  childenglish: "png",
  leftright2: "png",
};

// manifest.jsonが存在するアプリ一覧
const APPS_WITH_MANIFEST = [
  "smartenglish",
  "movieenglish",
  "aienglish",
  "englishegg",
  "childenglish",
  "leftright2",
];

// iOS「ホーム画面に追加」用のアプリ表示名マッピング
const APP_TITLES = {
  smartenglish: "Smart English",
  movieenglish: "映画英語",
  aienglish: "旅行英語",
  englishegg: "ビジネス英語",
  childenglish: "親子英語",
  leftright2: "長文talk",
};

function DynamicFaviconInner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const updateFavicon = () => {
      // 1. sessionStorageから取得
      let appName = appStorage.getAppName();

      // 2. sessionStorageにない場合、URLパラメータから取得
      if (!appName) {
        const appParam = searchParams.get("app");
        if (appParam) {
          appName = appParam.toLowerCase();
        }
      }

      // appNameが取得できない場合はSSRの値を保持（上書きしない）
      if (!appName) return;

      // faviconのパスを決定
      let faviconPath = "/favicon.ico"; // デフォルト
      if (appName && FAVICON_EXTENSIONS[appName]) {
        const ext = FAVICON_EXTENSIONS[appName];
        faviconPath = `/favicon-${appName}.${ext}`;
      }

      // 既存のfavicon linkタグを取得または作成
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }

      // faviconを更新
      link.href = faviconPath;

      // manifestを更新（なければ作成）
      // ※layout.jsに静的manifestリンクを置かず、ここで生成することでiOSが正しいmanifestをfetchするようにする
      let manifestLink = document.querySelector("link[rel='manifest']");
      if (!manifestLink) {
        manifestLink = document.createElement("link");
        manifestLink.rel = "manifest";
        document.head.appendChild(manifestLink);
      }
      let manifestPath = "/manifest.json"; // デフォルト
      if (appName && APPS_WITH_MANIFEST.includes(appName)) {
        manifestPath = `/manifest-${appName}.json`;
      }
      manifestLink.href = manifestPath;

      // apple-touch-iconを更新（iOS「ホーム画面に追加」用）
      let appleTouchIconLink = document.querySelector("link[rel='apple-touch-icon']");
      if (!appleTouchIconLink) {
        appleTouchIconLink = document.createElement("link");
        appleTouchIconLink.rel = "apple-touch-icon";
        document.head.appendChild(appleTouchIconLink);
      }
      if (appName && APPS_WITH_MANIFEST.includes(appName)) {
        appleTouchIconLink.href = `/apple-touch-icon-${appName}.png`;
      } else {
        appleTouchIconLink.href = "/apple-touch-icon.png";
      }

      // apple-mobile-web-app-title を更新（iOS「ホーム画面に追加」のアプリ名）
      let appTitleMeta = document.querySelector("meta[name='apple-mobile-web-app-title']");
      if (!appTitleMeta) {
        appTitleMeta = document.createElement("meta");
        appTitleMeta.name = "apple-mobile-web-app-title";
        document.head.appendChild(appTitleMeta);
      }
      const appDisplayTitle = (appName && APP_TITLES[appName]) ? APP_TITLES[appName] : "Smart English";
      appTitleMeta.content = appDisplayTitle;
    };

    // 初回実行
    updateFavicon();

    // sessionStorageの変更を監視（他タブからの変更は検知しないが、同一タブ内での変更を検知）
    const handleStorageChange = (e) => {
      if (e.key === "app_name") {
        updateFavicon();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [pathname, searchParams]);

  return null;
}

export default function DynamicFavicon() {
  return (
    <Suspense fallback={null}>
      <DynamicFaviconInner />
    </Suspense>
  );
}
